import React, { useEffect, useState, useCallback } from 'react';
import { Plus, RefreshCw, ShieldOff } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { listAllLicences, listPlans, listTenants, generateLicence, revokeLicence } from '../../utils/adminApi';

const EMPTY_FORM = {
  tenant_id: '', plan: 'starter',
  valid_from: '', valid_until: '', notes: '',
};

export default function LicencesPage() {
  const { adminKey }            = useAdminAuth();
  const [licences, setLicences] = useState([]);
  const [tenants, setTenants]   = useState([]);
  const [plans, setPlans]       = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter]     = useState('all');
  const [error, setError]       = useState('');
  const [actionError, setActionError] = useState('');

  // Revoke confirmation
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking]         = useState(false);

  const load = useCallback(() => {
    setError('');
    Promise.all([listAllLicences(adminKey), listTenants(adminKey), listPlans(adminKey)])
      .then(([l, t, p]) => { setLicences(l); setTenants(t); setPlans(p); })
      .catch(err => setError(err.message));
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  async function confirmRevoke() {
    setRevoking(true);
    setActionError('');
    try {
      await revokeLicence(adminKey, revokeTarget.key);
      setRevokeTarget(null);
      load();
    } catch (err) {
      setActionError(err.message);
      setRevokeTarget(null);
    } finally {
      setRevoking(false);
    }
  }

  async function handleGenerate(e) {
    e.preventDefault();
    setFormError('');
    if (!form.tenant_id) { setFormError('Select a tenant'); return; }
    if (!form.valid_from) { setFormError('Valid From is required'); return; }
    if (!form.valid_until) { setFormError('Valid Until is required'); return; }
    if (form.valid_until <= form.valid_from) { setFormError('Valid Until must be after Valid From'); return; }
    setSubmitting(true);
    try {
      await generateLicence(adminKey, form);
      setShowForm(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  const visible = licences.filter(l =>
    filter === 'all' ? true : filter === 'active' ? l.is_active : !l.is_active
  );

  if (error) return (
    <div className="admin-error-page">
      <p>{error}</p>
      <button className="admin-btn-secondary" onClick={load} style={{ marginTop: 12 }}>Retry</button>
    </div>
  );

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h2 className="admin-page-title">Licence Keys</h2>
        <div className="admin-page-actions">
          <button className="admin-btn-icon" onClick={load}><RefreshCw size={15} /></button>
          <button className="admin-btn-primary" onClick={() => setShowForm(true)}>
            <Plus size={15} /> Generate Key
          </button>
        </div>
      </div>

      {actionError && (
        <div className="admin-error" style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: 'var(--clr-error-bg, #fef2f2)', border: '1px solid var(--clr-error, #ef4444)' }}>
          {actionError}
        </div>
      )}

      <div className="admin-filter-tabs" style={{ marginBottom: 16 }}>
        {['all', 'active', 'revoked'].map(f => (
          <button key={f} className={`admin-filter-tab${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Key</th><th>Tenant</th><th>Plan</th><th>Users</th>
              <th>Valid From</th><th>Valid Until</th><th>Status</th><th>Notes</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={9} className="admin-table-empty">No licence keys found</td></tr>
            )}
            {visible.map(l => (
              <tr key={l.id}>
                <td><code className="admin-key">{l.key}</code></td>
                <td><code>{l.tenant_id}</code></td>
                <td><span className={`admin-tag plan-${l.plan}`}>{l.plan}</span></td>
                <td>{l.max_users}</td>
                <td>{l.valid_from}</td>
                <td>{l.valid_until}</td>
                <td><span className={`admin-badge ${l.is_active ? 'active' : 'inactive'}`}>{l.is_active ? 'Active' : 'Revoked'}</span></td>
                <td>{l.notes || <span className="admin-muted">—</span>}</td>
                <td>
                  {l.is_active && (
                    <button className="admin-btn-sm danger" onClick={() => setRevokeTarget(l)}>
                      <ShieldOff size={12} /> Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Revoke confirmation modal */}
      {revokeTarget && (
        <div className="admin-modal-backdrop" onClick={() => setRevokeTarget(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h3 className="admin-modal-title">Revoke Licence Key</h3>
            <p style={{ fontSize: 13, color: 'var(--clr-text-secondary)', lineHeight: 1.6, margin: '12px 0 4px' }}>
              Revoke key for tenant <strong>{revokeTarget.tenant_id}</strong>?
            </p>
            <p style={{ fontSize: 13, color: 'var(--clr-text-muted)', marginBottom: 20 }}>
              <code>{revokeTarget.key}</code>
            </p>
            <p style={{ fontSize: 13, color: 'var(--clr-error, #ef4444)', marginBottom: 20 }}>
              This may immediately block the tenant's login.
            </p>
            <div className="admin-form-actions">
              <button className="admin-btn-secondary" onClick={() => setRevokeTarget(null)}>Cancel</button>
              <button className="admin-btn-primary danger" onClick={confirmRevoke} disabled={revoking}>
                {revoking ? 'Revoking…' : 'Yes, Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate form */}
      {showForm && (
        <div className="admin-modal-backdrop" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setFormError(''); }}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h3 className="admin-modal-title">Generate Licence Key</h3>
            <form onSubmit={handleGenerate} className="admin-form">
              <div className="admin-form-row">
                <label>Tenant *
                  <select className="admin-input" value={form.tenant_id} onChange={e => setForm(f => ({...f, tenant_id: e.target.value}))}>
                    <option value="">— Select tenant —</option>
                    {tenants.map(t => <option key={t.tenant_id} value={t.tenant_id}>{t.name} ({t.tenant_id})</option>)}
                  </select>
                </label>
                <label>Plan *
                  <select className="admin-input" value={form.plan} onChange={e => setForm(f => ({...f, plan: e.target.value}))}>
                    {plans.map(p => <option key={p.plan} value={p.plan}>{p.description}</option>)}
                  </select>
                </label>
              </div>
              <div className="admin-form-row">
                <label>Valid From *
                  <input type="date" className="admin-input" value={form.valid_from}
                    onChange={e => setForm(f => ({...f, valid_from: e.target.value}))} />
                </label>
                <label>Valid Until *
                  <input type="date" className="admin-input" value={form.valid_until}
                    min={form.valid_from || undefined}
                    onChange={e => setForm(f => ({...f, valid_until: e.target.value}))} />
                </label>
              </div>
              <div className="admin-form-row">
                <label>Notes
                  <input className="admin-input" value={form.notes}
                    onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                    placeholder="e.g. Annual renewal 2026-27" />
                </label>
              </div>
              {formError && <p className="admin-error">{formError}</p>}
              <div className="admin-form-actions">
                <button type="button" className="admin-btn-secondary" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setFormError(''); }}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={submitting}>{submitting ? 'Generating…' : 'Generate'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
